/**
 * Access Control Utility for Transaction Detail Components
 *
 * This module provides role-based access control for different milestones
 * in Purchase and Listing transactions.
 *
 * Access Levels:
 * - READ_ONLY: User can only view the content
 * - ACTIONABLE: User can take actions (edit, complete, mark done, etc.)
 * - NO_ACCESS: User has no access to this milestone
 */

import { getActiveRoleFromStorage } from './getActiveRole';

// Role access maps for active milestones (Purchase transactions)
// TRANSACTION_COORDINATOR mirrors BUYER_AGENT here (a TC on a purchase transaction
// coordinates for the buyer's agent). This keeps an explicit TC participant record
// at the same access level getUserRoleInTransaction's non-participant fallback
// already granted TC — without it, a TC who is an explicit participant would fall
// through to NO_ACCESS below since this map previously had no TC key at all.
export const PURCHASE_ACTIVE_ACCESS = {
  OFFER_ACCEPTED: { BUYER: 'READ_ONLY', BUYER_AGENT: 'ACTIONABLE', SELLER: 'READ_ONLY', LISTING_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'ACTIONABLE' },
  SELLER_DISCLOSURES: { BUYER: 'READ_ONLY', BUYER_AGENT: 'ACTIONABLE', SELLER: 'ACTIONABLE', LISTING_AGENT: 'ACTIONABLE', TRANSACTION_COORDINATOR: 'ACTIONABLE' },
  EARNEST_MONEY_DEPOSITED: { BUYER: 'ACTIONABLE', BUYER_AGENT: 'ACTIONABLE', SELLER: 'READ_ONLY', LISTING_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'ACTIONABLE' },
  INSPECTION_CONTINGENCY: { BUYER: 'READ_ONLY', BUYER_AGENT: 'ACTIONABLE', SELLER: 'READ_ONLY', LISTING_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'ACTIONABLE' },
  APPRAISAL_CONTINGENCY: { BUYER: 'READ_ONLY', BUYER_AGENT: 'ACTIONABLE', SELLER: 'READ_ONLY', LISTING_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'ACTIONABLE' },
  LOAN_CONTINGENCY: { BUYER: 'READ_ONLY', BUYER_AGENT: 'ACTIONABLE', SELLER: 'READ_ONLY', LISTING_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'ACTIONABLE' },
  REPAIR_REQUESTS: { BUYER: 'READ_ONLY', BUYER_AGENT: 'ACTIONABLE', SELLER: 'READ_ONLY', LISTING_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'ACTIONABLE' },
  CONTINGENCY_REMOVAL: { BUYER: 'READ_ONLY', BUYER_AGENT: 'ACTIONABLE', SELLER: 'READ_ONLY', LISTING_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'ACTIONABLE' },
  FINAL_LOAN_DOCS: { BUYER: 'READ_ONLY', BUYER_AGENT: 'READ_ONLY', SELLER: 'READ_ONLY', LISTING_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'READ_ONLY' },
  FINAL_WALKTHROUGH: { BUYER: 'READ_ONLY', BUYER_AGENT: 'ACTIONABLE', SELLER: 'READ_ONLY', LISTING_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'ACTIONABLE' },
  CLOSE_OF_ESCROW: { BUYER: 'READ_ONLY', BUYER_AGENT: 'READ_ONLY', SELLER: 'READ_ONLY', LISTING_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'READ_ONLY' },
};

// Role access maps for active milestones (Listing transactions)
// TRANSACTION_COORDINATOR mirrors LISTING_AGENT here — see PURCHASE_ACTIVE_ACCESS
// comment above for why this key needs to exist explicitly.
export const LISTING_ACTIVE_ACCESS = {
  OFFER_ACCEPTED: { SELLER: 'READ_ONLY', LISTING_AGENT: 'ACTIONABLE', BUYER: 'READ_ONLY', BUYER_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'ACTIONABLE' },
  INSPECTION_PERIOD: { SELLER: 'READ_ONLY', LISTING_AGENT: 'ACTIONABLE', BUYER: 'READ_ONLY', BUYER_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'ACTIONABLE' },
  APPRAISAL_COMPLETED: { SELLER: 'READ_ONLY', LISTING_AGENT: 'ACTIONABLE', BUYER: 'READ_ONLY', BUYER_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'ACTIONABLE' },
  REPAIR_EXECUTION: { SELLER: 'ACTIONABLE', LISTING_AGENT: 'ACTIONABLE', BUYER: 'READ_ONLY', BUYER_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'ACTIONABLE' },
  BUYER_LOAN_APPROVAL: { SELLER: 'READ_ONLY', LISTING_AGENT: 'READ_ONLY', BUYER: 'READ_ONLY', BUYER_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'READ_ONLY' },
  CONTINGENCY_REMOVAL: { SELLER: 'READ_ONLY', LISTING_AGENT: 'ACTIONABLE', BUYER: 'READ_ONLY', BUYER_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'ACTIONABLE' },
  FINAL_WALKTHROUGH: { SELLER: 'READ_ONLY', LISTING_AGENT: 'ACTIONABLE', BUYER: 'READ_ONLY', BUYER_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'ACTIONABLE' },
  CLOSE_OF_ESCROW: { SELLER: 'READ_ONLY', LISTING_AGENT: 'READ_ONLY', BUYER: 'READ_ONLY', BUYER_AGENT: 'READ_ONLY', TRANSACTION_COORDINATOR: 'READ_ONLY' },
};

// Default access for milestones not in the access maps
export const DEFAULT_ACCESS = 'READ_ONLY';

// Access levels enum
export const ACCESS_LEVELS = {
  READ_ONLY: 'READ_ONLY',
  ACTIONABLE: 'ACTIONABLE',
  NO_ACCESS: 'NO_ACCESS',
};

/**
 * Normalize role string to match access map keys
 * @param {string} role - The role string to normalize
 * @returns {string} Normalized role string
 */
export const normalizeRole = (role) => {
  if (!role) return '';
  const normalized = role.toUpperCase().trim().replace(/[^A-Z_]/g, '_');

  // Map common variations to standard role names
  const roleMapping = {
    'BUYERS_AGENT': 'BUYER_AGENT',
    'BUYER_S_AGENT': 'BUYER_AGENT',
    'BUYING_AGENT': 'BUYER_AGENT',
    'SELLERS_AGENT': 'LISTING_AGENT',
    'SELLER_S_AGENT': 'LISTING_AGENT',
    'SELLING_AGENT': 'LISTING_AGENT',
    // Co-agent participant roles — both get equal agent-level permissions (matches mobile)
    'CO_LISTING_AGENT': 'LISTING_AGENT',
    'CO_BUYERS_AGENT': 'BUYER_AGENT',
    // Client role aliases
    'HOMEOWNER': 'SELLER',
    'OWNER': 'SELLER',
    'CLIENT': 'BUYER',
    'PURCHASER': 'BUYER',
  };

  return roleMapping[normalized] || normalized;
};

/**
 * Normalize milestone name to match access map keys
 * @param {string} milestoneName - The milestone name to normalize
 * @returns {string} Normalized milestone name
 */
export const normalizeMilestone = (milestoneName) => {
  if (!milestoneName) return '';
  return milestoneName.toUpperCase().trim().replace(/[\s-]+/g, '_').replace(/[^A-Z_]/g, '');
};

/**
 * Get logged-in user information from localStorage
 * @returns {Object|null} User object or null if not found
 */
export const getUserInfo = () => {
  try {
    const userData = localStorage.getItem('user');
    if (userData) {
      return JSON.parse(userData);
    }
  } catch (error) {
    console.error('Error parsing user data:', error);
  }
  return null;
};

/**
 * Check whether the logged-in user has a backend-granted permission,
 * aggregated across ALL of their roles. Reads the `permissions` array
 * from /users/me cached in localStorage. Wildcard '*' — granted to
 * ADMIN — matches every check.
 *
 * Note: this returns true if ANY of the user's roles grants the
 * permission. Use hasPermissionForRole() when you need the check
 * scoped to the currently-active role only (e.g. a multi-role user
 * who is Agent+Seller and has switched to Seller mode should NOT
 * see agent-only UI).
 * @param {string} permission The coarse-grained permission name (e.g. 'inbox.view')
 * @returns {boolean}
 */
export const hasPermission = (permission) => {
  const user = getUserInfo();
  const perms = user?.permissions;
  if (!Array.isArray(perms)) return false;
  return perms.includes('*') || perms.includes(permission);
};

/**
 * Check whether a specific role holds a permission. Uses the per-role
 * `rolePermissions` map from /users/me. Correct choice for UI gates
 * that should reflect the user's currently-active role rather than
 * the union across all their roles.
 * @param {string|null|undefined} role The role to check (e.g. 'AGENT')
 * @param {string} permission The coarse-grained permission name
 * @returns {boolean}
 */
export const hasPermissionForRole = (role, permission) => {
  if (!role) return false;
  const user = getUserInfo();
  const rolePerms = user?.rolePermissions?.[role];
  if (!Array.isArray(rolePerms)) return false;
  return rolePerms.includes('*') || rolePerms.includes(permission);
};

/**
 * Get the current user's role in a specific transaction
 * @param {Object} transaction - The transaction object
 * @param {Array} participants - Array of participants in the transaction
 * @returns {string} The user's role in the transaction (BUYER, SELLER, BUYER_AGENT, LISTING_AGENT, or empty string)
 */
// Session (Switch Role) role → the participant roles (after normalizeRole) it
// acts through on a transaction. Used to pick between duplicate rows for one user.
const ACTIVE_ROLE_TO_PARTICIPANT_ROLES = {
  AGENT: ['BUYER_AGENT', 'LISTING_AGENT', 'AGENT'],
  BUYER_AGENT: ['BUYER_AGENT'],
  LISTING_AGENT: ['LISTING_AGENT'],
  TRANSACTION_COORDINATOR: ['TRANSACTION_COORDINATOR'],
  BROKER: ['BROKER'],
  BROKER_MANAGER: ['BROKER'],
  BROKER_ADMIN: ['BROKER'],
  BROKERAGE: ['BROKER'],
  BUYER: ['BUYER'],
  SELLER: ['SELLER'],
};

export const getUserRoleInTransaction = (transaction, participants = []) => {
  const user = getUserInfo();
  if (!user) return '';

  const userEmail = user.email?.toLowerCase();
  const userId = user.id || user.uid;
  const userFirebaseUid = user.firebaseUid || user.uid;

  // First, check if user is a participant in the transaction
  const participantRole = (p) => normalizeRole(p.role || p.participantRole || p.userRole || p.type || '');
  const matches = participants.filter(p => {
    const participantEmail = p.email?.toLowerCase();
    const participantId = p.id || p.odooId || p.userId || p.firebaseUid;
    return (userEmail && participantEmail === userEmail)
      || (userId && (participantId === userId || p.id === userId || p.userId === userId))
      || (userFirebaseUid && (p.firebaseUid === userFirebaseUid || p.userId === userFirebaseUid));
  });
  // A user can appear more than once (e.g. an agent who is also their own
  // brokerage's BROKER). Pick the row matching the role they're acting as in
  // this session (Switch Role), then one that grants agent/TC access, then
  // the first match — never just list order.
  const activeRole = getActiveRoleFromStorage();
  const rowsForActiveRole = ACTIVE_ROLE_TO_PARTICIPANT_ROLES[activeRole] || [];
  const MANAGER_ROLES = ['AGENT', 'BUYER_AGENT', 'LISTING_AGENT', 'TRANSACTION_COORDINATOR'];
  const userParticipant = matches.find(p => rowsForActiveRole.includes(participantRole(p)))
    ?? matches.find(p => MANAGER_ROLES.includes(participantRole(p)))
    ?? matches[0];

  if (userParticipant) {
    return participantRole(userParticipant);
  }

  // If user is not found in participants but has agent role, determine based on transaction type
  const userRoles = user.roles || user.role || user.userType || '';
  const isAgent = Array.isArray(userRoles)
    ? userRoles.some(r => r?.toUpperCase() === 'AGENT' || r?.toUpperCase() === 'TRANSACTION_COORDINATOR')
    : (userRoles?.toUpperCase() === 'AGENT' || userRoles?.toUpperCase() === 'TRANSACTION_COORDINATOR');

  if (isAgent && transaction) {
    const transactionType = transaction.type?.toUpperCase() || transaction.side?.toUpperCase() || '';
    // For purchase transactions, the agent is likely the buyer's agent
    if (transactionType === 'PURCHASE' || transactionType === 'BUYING') {
      return 'BUYER_AGENT';
    }
    // For listing transactions, the agent is likely the listing agent
    if (transactionType === 'LISTING' || transactionType === 'SALE') {
      return 'LISTING_AGENT';
    }
  }

  // Fallback for client users (seller/buyer) when participant matching fails
  if (transaction) {
    const globalRolesStr = Array.isArray(userRoles)
      ? userRoles.join(' ').toUpperCase()
      : (userRoles?.toUpperCase() || '');
    const txnType = (transaction.type || transaction.side || transaction.transactionType || '').toUpperCase();
    if (globalRolesStr.includes('SELLER') && (txnType === 'LISTING' || txnType === 'SALE')) return 'SELLER';
    if (globalRolesStr.includes('BUYER') && (txnType === 'PURCHASE' || txnType === 'BUYING')) return 'BUYER';
  }

  return '';
};

/**
 * Get access level for a specific milestone based on user role and transaction type
 * @param {string} milestoneName - The name of the milestone
 * @param {string} userRole - The user's role in the transaction
 * @param {string} transactionType - The type of transaction (PURCHASE or LISTING)
 * @returns {string} Access level (ACTIONABLE, READ_ONLY, or NO_ACCESS)
 */
export const getMilestoneAccess = (milestoneName, userRole, transactionType) => {
  if (!milestoneName || !userRole) {
    return ACCESS_LEVELS.READ_ONLY;
  }

  const normalizedMilestone = normalizeMilestone(milestoneName);
  const normalizedRole = normalizeRole(userRole);
  const normalizedType = transactionType?.toUpperCase() || '';

  // Determine which access map to use based on transaction type
  let accessMap;
  if (normalizedType === 'PURCHASE' || normalizedType === 'BUYING') {
    accessMap = PURCHASE_ACTIVE_ACCESS;
  } else if (normalizedType === 'LISTING' || normalizedType === 'SALE') {
    accessMap = LISTING_ACTIVE_ACCESS;
  } else {
    // Default to read-only if transaction type is unknown
    return ACCESS_LEVELS.READ_ONLY;
  }

  // Get access level from the map
  const milestoneAccess = accessMap[normalizedMilestone];
  if (!milestoneAccess) {
    // Milestone not in access map, default to READ_ONLY
    return ACCESS_LEVELS.READ_ONLY;
  }

  const access = milestoneAccess[normalizedRole];
  if (!access) {
    // Role not found for this milestone, default to NO_ACCESS
    return ACCESS_LEVELS.NO_ACCESS;
  }

  return access;
};

/**
 * Check if user can perform actions on a milestone
 * @param {string} milestoneName - The name of the milestone
 * @param {string} userRole - The user's role in the transaction
 * @param {string} transactionType - The type of transaction
 * @returns {boolean} True if user can perform actions
 */
export const canPerformActions = (milestoneName, userRole, transactionType) => {
  const access = getMilestoneAccess(milestoneName, userRole, transactionType);
  return access === ACCESS_LEVELS.ACTIONABLE;
};

/**
 * Check if user can view a milestone
 * @param {string} milestoneName - The name of the milestone
 * @param {string} userRole - The user's role in the transaction
 * @param {string} transactionType - The type of transaction
 * @returns {boolean} True if user can view
 */
export const canView = (milestoneName, userRole, transactionType) => {
  const access = getMilestoneAccess(milestoneName, userRole, transactionType);
  return access === ACCESS_LEVELS.ACTIONABLE || access === ACCESS_LEVELS.READ_ONLY;
};

/**
 * Get access control object for component use
 * @param {Object} transaction - The transaction object
 * @param {Array} participants - Array of participants
 * @returns {Object} Access control object with helper methods
 */
export const getAccessControl = (transaction, participants = []) => {
  const userRole = getUserRoleInTransaction(transaction, participants);
  const transactionType = transaction?.type || transaction?.side || '';

  return {
    userRole,
    transactionType,

    /**
     * Check if user can perform actions on a specific milestone
     * @param {string} milestoneName - The milestone name
     * @returns {boolean}
     */
    canAction: (milestoneName) => canPerformActions(milestoneName, userRole, transactionType),

    /**
     * Check if user can only view (read-only) a specific milestone
     * @param {string} milestoneName - The milestone name
     * @returns {boolean}
     */
    isReadOnly: (milestoneName) => {
      const access = getMilestoneAccess(milestoneName, userRole, transactionType);
      return access === ACCESS_LEVELS.READ_ONLY;
    },

    /**
     * Get the access level for a specific milestone
     * @param {string} milestoneName - The milestone name
     * @returns {string} Access level
     */
    getAccess: (milestoneName) => getMilestoneAccess(milestoneName, userRole, transactionType),

    /**
     * Check if user has actionable access for general transaction actions
     * (like inviting parties, creating documents, etc.)
     * @returns {boolean}
     */
    canManageTransaction: () => {
      return userRole === 'AGENT' || userRole === 'BUYER_AGENT' || userRole === 'LISTING_AGENT' || userRole === 'TRANSACTION_COORDINATOR';
    },

    /**
     * Check if user is a client (buyer or seller)
     * @returns {boolean}
     */
    isClient: () => {
      return userRole === 'BUYER' || userRole === 'SELLER';
    },

    /**
     * Check if user is an agent (or transaction coordinator)
     * @returns {boolean}
     */
    isAgent: () => {
      return userRole === 'AGENT' || userRole === 'BUYER_AGENT' || userRole === 'LISTING_AGENT' || userRole === 'TRANSACTION_COORDINATOR';
    },
  };
};

export default {
  PURCHASE_ACTIVE_ACCESS,
  LISTING_ACTIVE_ACCESS,
  ACCESS_LEVELS,
  getUserInfo,
  hasPermission,
  hasPermissionForRole,
  getUserRoleInTransaction,
  getMilestoneAccess,
  canPerformActions,
  canView,
  getAccessControl,
  normalizeRole,
  normalizeMilestone,
};
